import { Box } from "@outschool/backpack";
import {
  ClassDetailsQuery_ActivityFragment,
  ClassDetailsQuery_SectionFragment
} from "@outschool/gql-frontend-generated";
import { useSession } from "@outschool/ui-auth";
import { Space } from "@outschool/ui-legacy-component-library";
import { useIsMobile, useIsMounted } from "@outschool/ui-utils";
import React from "react";

import { SectionCallToAction } from "../ctas/SectionCallToActionButtons";
import useClassDetailsSections from "../hooks/useClassDetailsSections";
import AvailableTimesHeader from "./AvailableTimesHeader";
import ChooseAnotherTime from "./ChooseAnotherTime";
import ChooseAnotherTimeLoading from "./ChooseAnotherTimeLoading";
import HighDemandBanner from "./HighDemandBanner";
import SectionTable from "./SectionTable";
import UnavailableActivityBanner from "./UnavailableActivityBanner";

enum State {
  Loading = "Loading",
  LoadedNoAvailability = "LoadedNoAvailability",
  LoadedHasAvailability = "LoadedHasAvailability"
}

function useAvailabilityState({
  activity,
  isLoading,
  sections
}: {
  activity: ClassDetailsQuery_ActivityFragment;
  isLoading: boolean;
  sections: ClassDetailsQuery_SectionFragment[];
}): State {
  const isMounted = useIsMounted();
  if (!isMounted) {
    return State.Loading;
  }
  if (isLoading) {
    return State.Loading;
  }
  return sections.length > 0 && activity.isPublished
    ? State.LoadedHasAvailability
    : State.LoadedNoAvailability;
}

export default function AvailableTimes({
  activity,
  isOrganizationEnrollment,
  onClickMoreLikeThis
}: {
  activity: ClassDetailsQuery_ActivityFragment;
  isOrganizationEnrollment: any;
  onClickMoreLikeThis: () => void;
}) {
  const { currentUser, userTimeZone } = useSession();
  const isMobile = useIsMobile();

  // section fetching
  const { fetchMore, paginatedFilteredSections, isLoading } =
    useClassDetailsSections({
      activityWithoutSections: activity,
      skip:
        activity.upcomingAvailableSectionCount === 0 &&
        !activity.allowWaitlist &&
        !activity.allows_late_enrollments_for_fixed_length_classes
    });

  const sections = paginatedFilteredSections?.data ?? [];
  const totalCount = paginatedFilteredSections?.totalCount ?? 0;
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);
  const fetchNextPage = React.useCallback(
    async (offset: number) => {
      setIsLoadingMore(true);
      await fetchMore({
        variables: {
          offset
        },
        updateQuery: (_previousResult: any, { fetchMoreResult }: any) => {
          return fetchMoreResult;
        }
      });
      setIsLoadingMore(false);
    },
    [fetchMore]
  );

  const state = useAvailabilityState({ activity, sections, isLoading });
  switch (state) {
    case State.Loading:
    case State.LoadedHasAvailability:
      return (
        <Box>
          <Box
            sx={{
              height: 4,
              width: 32,
              backgroundColor: "warning.500",
              marginBottom: 10
            }}
          />
          <AvailableTimesHeader
            sectionCount={totalCount}
            userTimeZone={userTimeZone!}
          />
          <HighDemandBanner
            upcomingAvailableSectionCount={
              activity?.upcomingAvailableSectionCount
            }
            upcomingSectionCount={activity?.upcomingSectionCount}
          />
          <Space y="medium" />
          <SectionTable
            isLoading={isLoading || isLoadingMore}
            activity={activity}
            sections={sections || []}
            totalCount={totalCount}
            fetchNextPage={fetchNextPage}
            userTimeZone={userTimeZone!}
            CtaComponent={({ section, size, disabled }) => (
              <SectionCallToAction
                sx={{ width: isMobile ? "100%" : "inherit" }}
                activity={activity}
                parentUid={currentUser?.uid || undefined}
                section={section}
                isOrganizationEnrollment={isOrganizationEnrollment}
                variant="contained"
                size={size}
                disabled={disabled}
                trackingSharedProperties={{
                  component: "AvailableTimes"
                }}
              />
            )}
          />
          <Space y="small" />
          {state === State.Loading ? (
            <ChooseAnotherTimeLoading />
          ) : (
            <ChooseAnotherTime
              activity={activity}
              leader={activity.leader}
              showDescription={!isMobile}
            />
          )}
        </Box>
      );
    case State.LoadedNoAvailability:
      return (
        <Box sx={{ marginTop: "1em" }}>
          <AvailableTimesHeader
            sectionCount={totalCount}
            userTimeZone={userTimeZone!}
          />
          <UnavailableActivityBanner
            activity={activity}
            onClickMoreLikeThis={onClickMoreLikeThis}
          />
        </Box>
      );
  }
}
